/**
 * Event time formatting — the single place the browser turns a meeting's stored
 * instant into text.
 *
 * The rule, and the only rule: an event is always shown in the EVENT'S OWN
 * timezone, never the viewer's. `meetings.start_at` is a UTC instant and
 * `meetings.timezone` is the IANA zone it was scheduled in, so a bare
 * `new Date(iso).toLocaleTimeString()` renders whatever zone the reader's laptop
 * happens to be set to — which is how a 5:30 PM London event came to read as
 * 11:00 PM to staff in India.
 *
 * Every function here therefore takes `tz` as a REQUIRED argument. Passing
 * `undefined` to Intl means "use the browser's zone", so there is deliberately
 * no default: a caller with no zone to hand must say `"UTC"` out loud.
 *
 * Mirrors `backend/app/core/meeting_utils.py` (`_FRIENDLY_TIMEZONE_LABELS`,
 * `format_when`) so an event reads the same in the browser and in its email.
 */

/** Short, human labels for zones whose `%Z` abbreviation is unhelpful or absent.
 *  Mirror of `_FRIENDLY_TIMEZONE_LABELS` in backend/app/core/meeting_utils.py. */
const FRIENDLY_TIMEZONE_LABELS: Record<string, string> = {
    "Asia/Dubai": "Dubai",
    "Asia/Kolkata": "India",
    "Asia/Singapore": "Singapore",
    "Asia/Tokyo": "Tokyo",
    "Asia/Hong_Kong": "Hong Kong",
    "Asia/Shanghai": "China",
    "Asia/Seoul": "Seoul",
    "Asia/Kuala_Lumpur": "Malaysia",
    "Asia/Jakarta": "Jakarta",
    "Europe/London": "UK",
    "Europe/Paris": "Paris",
    "Europe/Berlin": "Berlin",
    "America/New_York": "ET",
    "America/Chicago": "CT",
    "America/Denver": "MT",
    "America/Los_Angeles": "PT",
    "America/Phoenix": "Arizona",
    "America/Anchorage": "Alaska",
    "Pacific/Honolulu": "Hawaii",
    "Australia/Sydney": "Sydney",
    "Australia/Melbourne": "Melbourne",
    "Australia/Brisbane": "Brisbane",
    "Australia/Perth": "Perth",
    "Pacific/Auckland": "Auckland",
    "Africa/Johannesburg": "South Africa",
    "America/Sao_Paulo": "Brazil",
    "America/Mexico_City": "Mexico City",
    UTC: "UTC",
};

/** A zone we can safely hand to Intl. Never returns the browser's zone. */
export function safeZone(tz: string | null | undefined): string {
    const candidate = (tz || "").trim();
    if (!candidate) return "UTC";
    try {
        new Intl.DateTimeFormat("en-US", { timeZone: candidate });
        return candidate;
    } catch {
        return "UTC";
    }
}

/** "UK", "Dubai", "PT" — the label used alongside a time. */
export function friendlyTimezoneLabel(tz: string | null | undefined): string {
    const zone = safeZone(tz);
    const known = FRIENDLY_TIMEZONE_LABELS[zone];
    if (known) return known;
    const city = zone.split("/").pop()?.replace(/_/g, " ");
    return city || zone;
}

function parse(iso: string | null | undefined): Date | null {
    if (!iso) return null;
    const value = new Date(iso);
    return Number.isNaN(value.getTime()) ? null : value;
}

function partsIn(value: Date, tz: string, options: Intl.DateTimeFormatOptions): string {
    try {
        return new Intl.DateTimeFormat("en-US", { ...options, timeZone: safeZone(tz) }).format(value);
    } catch {
        return "";
    }
}

/** e.g. "Sep 23, 2026". `options` overrides the default date style. */
export function formatEventDate(
    iso: string | null | undefined,
    tz: string | null | undefined,
    options?: Intl.DateTimeFormatOptions,
): string {
    const value = parse(iso);
    if (!value) return "";
    return partsIn(value, safeZone(tz), options ?? { month: "short", day: "numeric", year: "numeric" });
}

/** e.g. "5:30 PM" — no zone label; use `formatEventTimeRange` when one is wanted. */
export function formatEventTime(
    iso: string | null | undefined,
    tz: string | null | undefined,
): string {
    const value = parse(iso);
    if (!value) return "";
    return partsIn(value, safeZone(tz), { hour: "numeric", minute: "2-digit" });
}

/** e.g. "5:30 PM – 9:00 PM UK". Falls back to the start time alone when there is no end. */
export function formatEventTimeRange(
    startIso: string | null | undefined,
    endIso: string | null | undefined,
    tz: string | null | undefined,
    { withLabel = true, separator = " – " }: { withLabel?: boolean; separator?: string } = {},
): string {
    const zone = safeZone(tz);
    const start = formatEventTime(startIso, zone);
    if (!start) return "";
    const end = formatEventTime(endIso, zone);
    const label = withLabel ? ` ${friendlyTimezoneLabel(zone)}` : "";
    return end ? `${start}${separator}${end}${label}` : `${start}${label}`;
}

/** The `%Z`-style abbreviation for `tz` **at the event's own instant** — "IST",
 *  "GMT+5:30", "PDT". Derived from the instant and not from `new Date()`,
 *  because a September (BST) event viewed in December was captioned "GMT". */
export function timezoneAbbreviation(
    iso: string | null | undefined,
    tz: string | null | undefined,
): string {
    const zone = safeZone(tz);
    try {
        const basis = parse(iso) ?? new Date();
        return (
            new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "short" })
                .formatToParts(basis)
                .find((p) => p.type === "timeZoneName")?.value ?? zone
        );
    } catch {
        return zone;
    }
}

/** The calendar parts of an event's own day — for date chips that would otherwise
 *  use `getMonth()`/`getDate()` and land on the viewer's calendar. */
export function eventDateParts(
    iso: string | null | undefined,
    tz: string | null | undefined,
): { month: string; day: string; year: string } | null {
    const value = parse(iso);
    if (!value) return null;
    const zone = safeZone(tz);
    return {
        month: partsIn(value, zone, { month: "short" }),
        day: partsIn(value, zone, { day: "2-digit" }),
        year: partsIn(value, zone, { year: "numeric" }),
    };
}

/** `YYYY-MM-DD` for the given instant in the given zone. Use this instead of
 *  `toDateString()` whenever two dates are compared for "same day".
 *  Built from `formatToParts` rather than a formatted string, so it does not
 *  depend on any locale's field order. */
export function eventDayKey(
    iso: string | null | undefined,
    tz: string | null | undefined,
): string | null {
    const value = parse(iso);
    if (!value) return null;
    try {
        const parts = new Intl.DateTimeFormat("en-US", {
            timeZone: safeZone(tz),
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        }).formatToParts(value);
        const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
        const [y, m, d] = [get("year"), get("month"), get("day")];
        return y && m && d ? `${y}-${m}-${d}` : null;
    } catch {
        return null;
    }
}

/** True when the two instants fall on the same calendar day **in `tz`**. */
export function isSameEventDay(
    aIso: string | null | undefined,
    bIso: string | null | undefined,
    tz: string | null | undefined,
): boolean {
    const a = eventDayKey(aIso, tz);
    const b = eventDayKey(bIso, tz);
    return Boolean(a && b && a === b);
}

/** Whole calendar days from today to the event, counted in the event's zone —
 *  not `ceil(ms / 86_400_000)`, which calls an event 20 hours away "tomorrow". */
export function calendarDaysUntil(
    iso: string | null | undefined,
    tz: string | null | undefined,
    now: Date = new Date(),
): number | null {
    const target = eventDayKey(iso, tz);
    const today = eventDayKey(now.toISOString(), tz);
    if (!target || !today) return null;
    const diffMs = Date.parse(`${target}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`);
    return Math.round(diffMs / 86_400_000);
}

// A clock time with an am/pm marker. Deliberately narrow: "12:30" alone could be
// a price or a section number, but "12:30 PM" in event copy is a claim about
// when the event is.
const CLOCK_CLAIM_RE = /\b(\d{1,2}):(\d{2})\s*([AaPp])\.?[Mm]\.?\b/g;

// A 24-hour time presented as UTC, as the "(16:30 - 20:00 UTC)" bracket does.
// Mirror of `_UTC_CLAIM_RE` in backend/app/core/meeting_utils.py. Without this,
// copy whose only wrong time sits inside that bracket passes the editor's
// warning and is then refused by the backend with a 409 — which is the exact
// surprise the warning exists to prevent.
const UTC_CLAIM_RE = /\b(\d{1,2}):(\d{2})\b(?=[^()]*\bUTC\b)/g;

/** Times the copy asserts that the event record does not have, as 24h "HH:MM".
 *
 *  Mirror of `copy_time_conflicts` in backend/app/core/meeting_utils.py, which
 *  is what actually refuses the approval — this exists so the editor can warn
 *  before staff hit that refusal. Kept deliberately simple and in step with the
 *  backend; the backend remains the authority.
 */
export function copyTimeConflicts(
    text: string | null | undefined,
    startIso: string | null | undefined,
    endIso: string | null | undefined,
    tz: string | null | undefined,
): string[] {
    if (!text || !startIso) return [];
    const zone = safeZone(tz);

    const clock24 = (iso: string | null | undefined, inZone: string) => {
        const value = parse(iso);
        if (!value) return null;
        return partsIn(value, inZone, { hour: "2-digit", minute: "2-digit", hour12: false });
    };

    const permitted = new Set<string>();
    for (const iso of [startIso, endIso]) {
        for (const inZone of [zone, "UTC"]) {
            const stamp = clock24(iso, inZone);
            if (stamp) permitted.add(stamp);
        }
    }

    const claims = new Set<string>();
    for (const match of text.matchAll(CLOCK_CLAIM_RE)) {
        let hour = Number(match[1]) % 12;
        if (match[3].toLowerCase() === "p") hour += 12;
        claims.add(`${String(hour).padStart(2, "0")}:${match[2]}`);
    }
    for (const match of text.matchAll(UTC_CLAIM_RE)) {
        claims.add(`${String(Number(match[1])).padStart(2, "0")}:${match[2]}`);
    }

    return [...claims].filter((claim) => !permitted.has(claim)).sort();
}

/** The UTC offset, in minutes, that `tz` was on at a given instant. */
function offsetMinutesAt(instant: Date, tz: string): number {
    try {
        const parts = new Intl.DateTimeFormat("en-US", {
            timeZone: safeZone(tz),
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
        }).formatToParts(instant);
        const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
        let hour = get("hour");
        if (hour === 24) hour = 0;
        const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), hour, get("minute"), get("second"));
        return (asUtc - instant.getTime()) / 60_000;
    } catch {
        return 0;
    }
}

/** Turn a wall-clock date + time in `tz` into the UTC instant it names.
 *
 * The event form collects "2026-09-23" and "17:30" with no offset, and the
 * server stamps them with the event's zone. This is the browser-side mirror of
 * that, used to preview what will actually be stored. Two passes settle the
 * DST case where the offset at the guessed instant differs from the offset at
 * the real one.
 *
 * Returns null for an invalid date/time. During a spring-forward gap the named
 * wall-clock does not exist; the result then lands on the instant immediately
 * after the jump, which is what calendars do.
 */
export function wallClockToIso(
    date: string,
    time: string,
    tz: string | null | undefined,
): string | null {
    if (!date || !time) return null;
    const naiveUtc = Date.parse(`${date}T${time}:00Z`);
    if (Number.isNaN(naiveUtc)) return null;
    const zone = safeZone(tz);
    let instant = new Date(naiveUtc - offsetMinutesAt(new Date(naiveUtc), zone) * 60_000);
    instant = new Date(naiveUtc - offsetMinutesAt(instant, zone) * 60_000);
    return instant.toISOString();
}

/** The one-line "local (UTC)" string, matching `format_when` in the backend:
 *  "Wednesday, 23 September 2026, 5:30 PM - 9:00 PM UK (16:30 - 20:00 UTC)". */
export function formatEventWhen(
    startIso: string | null | undefined,
    endIso: string | null | undefined,
    tz: string | null | undefined,
    { weekday = false }: { weekday?: boolean } = {},
): string {
    const start = parse(startIso);
    if (!start) return "TBD";
    const zone = safeZone(tz);
    const label = friendlyTimezoneLabel(zone);
    // Assembled from parts rather than a locale's own ordering so it matches the
    // backend's "%A, %d %B %Y" exactly — this string is compared against the one
    // in the event's email.
    let dateStr = "";
    try {
        const p = new Intl.DateTimeFormat("en-GB", {
            timeZone: zone,
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
        }).formatToParts(start);
        const get = (type: string) => p.find((x) => x.type === type)?.value ?? "";
        const body = `${get("day")} ${get("month")} ${get("year")}`;
        dateStr = weekday ? `${get("weekday")}, ${body}` : body;
    } catch {
        return "TBD";
    }
    const localTimes = formatEventTimeRange(startIso, endIso, zone, { withLabel: false, separator: " - " });
    if (label === "UTC") return `${dateStr}, ${localTimes} UTC`;
    // UTC is written 24-hour in brackets, as the backend's format_when does.
    const clock24 = (iso: string | null | undefined) => {
        const value = parse(iso);
        return value ? partsIn(value, "UTC", { hour: "2-digit", minute: "2-digit", hour12: false }) : "";
    };
    const utcStart = clock24(startIso);
    const utcEnd = clock24(endIso);
    const utcTimes = utcEnd ? `${utcStart} - ${utcEnd}` : utcStart;
    return `${dateStr}, ${localTimes} ${label} (${utcTimes} UTC)`;
}

/** Join an event's structured address into one line, mirroring
 *  `build_location_string` in backend/app/core/meeting_utils.py.
 *
 *  Repeats are dropped case-insensitively: organisers with no state routinely
 *  re-enter the city there, and the un-deduped join read "London, London".
 */
export function formatEventAddress(
    details: Record<string, string | null | undefined> | null | undefined,
): string {
    if (!details) return "";
    const ordered = [
        details.venue_name,
        details.address_line_1,
        details.address_line_2,
        details.city,
        details.state,
        details.pincode,
        details.country,
    ];
    const seen = new Set<string>();
    const parts: string[] = [];
    for (const raw of ordered) {
        const part = (raw || "").trim();
        if (!part) continue;
        const key = part.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        parts.push(part);
    }
    return parts.join(", ");
}
